/*
 * <cart-attach-add> — the add transport for the Robotryx "finish the build"
 * attach band (snippets/cart-attach-offer.liquid), and since 2026-10-09 for
 * the empty-drawer shelf's "Add" (snippets/cart-empty-shelf.liquid), which
 * also relies on the syncEmptyState(false) below to turn the empty drawer
 * into the cart in the same render.
 * Spec: docs/ROBOTRYX_EXTRAS_ATTACH_BUILD_BRIEF.md §8 (transport T2).
 *
 * ── WHY THIS EXISTS AND NOT <product-form> ────────────────────────────────────
 * The band sits INSIDE a cart line, and cart lines live inside <form
 * id="CartDrawer-Form"> / <form id="cart">. A nested <form> start tag is dropped
 * by the HTML parser while its </form> closes the OUTER form, and
 * product-form.js:6 then gets null from querySelector('form') and throws in its
 * constructor. So this element carries NO form at all: a button, data attributes
 * and a hand-built FormData.
 *
 * That is also the fix for D1 (the stale /cart subtotal). product-form.js hands
 * its section list to whatever cart component it finds, and CartDrawer's list
 * (cart-drawer.js) is only #CartDrawer + cart-icon-bubble — no #main-cart-footer,
 * so nothing else in the theme refreshes the subtotal on a cart-add.
 * This element takes its list from CartRegions.sectionsToRender()
 * (assets/cart-regions.js), which names every cart region present on the page,
 * #main-cart-footer included, so the subtotal can never go stale.
 *
 * ── IT NEVER OPENS THE DRAWER ─────────────────────────────────────────────────
 * cart-drawer.js's renderContents() calls open() unconditionally, which is what
 * made the drawer slide over the cart page in D1. This element never calls open()
 * and it does not need to: the drawer's own band is only reachable while the
 * drawer is already on screen (snippets/cart-drawer.liquid:45-47 keeps .drawer at
 * visibility:hidden until .active, so nothing inside is clickable or focusable
 * before that), and the /cart band is not in the drawer at all.
 * It does not need to re-bind #CartDrawer-Overlay either: CartRegions swaps
 * .drawer__inner, and the overlay sits inside #CartDrawer but outside that node
 * (snippets/cart-drawer.liquid:61-63), so the handler survives. renderContents()
 * still re-binds it because it replaces #CartDrawer wholesale.
 *
 * ── IT PUBLISHES cartUpdate WITH source 'cart-items' ──────────────────────────
 * Not a provenance claim — it is the render-suppression flag cart.js:31 keys on,
 * and the same one CartItems.updateQuantity publishes after rendering its own
 * regions. Any other value sends <cart-drawer-items> off on a needless refetch a
 * round-trip after the add, detaching the focus target afterAdd() is about to
 * use. Full reasoning at the publish site.
 *
 * ── NO MODULE-LEVEL STATE (D2) ────────────────────────────────────────────────
 * The announcement and the focus target are derived from THIS element's own data
 * attributes inside one click's closure. The previous inline M7 script kept an
 * `announce` variable at module scope that survived a failed fetch, so the next
 * unrelated product-form add announced the wrong product and stole focus. There
 * is nothing here for a later add to inherit.
 *
 * Globals used: routes / fetchConfig / publish / subscribe-side PUB_SUB_EVENTS /
 * trapFocus / window.cartStrings — all from constants.js, pubsub.js and global.js,
 * which layout/theme.liquid loads in <head> — plus CartRegions from
 * assets/cart-regions.js, tagged immediately before this file at both load
 * points. Loaded with defer from snippets/cart-drawer.liquid and
 * sections/main-cart-items.liquid.
 */
if (!customElements.get('cart-attach-add')) {
  customElements.define(
    'cart-attach-add',
    class CartAttachAdd extends HTMLElement {
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
      }

      connectedCallback() {
        this.button = this.querySelector('[data-attach-add]');
        if (this.button) this.button.addEventListener('click', this.onClick);
      }

      disconnectedCallback() {
        if (this.button) this.button.removeEventListener('click', this.onClick);
      }

      onClick(event) {
        event.preventDefault();
        if (this.button.getAttribute('aria-disabled') === 'true') return;

        const variantId = this.dataset.variantId;
        if (!variantId) return;

        // Captured now, before the fetch: the re-render detaches every node below
        // this point, and the surface the shopper is actually looking at is what
        // decides where focus and the announcement land. On /cart both bands
        // exist, so "am I in the drawer" cannot be inferred from the page.
        const inDrawer = !!this.closest('cart-drawer');
        const announcement = this.dataset.announce || '';
        const sections = CartRegions.sectionsToRender();

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');
        if (this.dataset.attachSource) {
          // The property's KEY is _attach_source unless the caller names one
          // (data-property-key). The empty-drawer shelf writes _xsell_source,
          // the key the PDP's Pack Choice rows already write, so one
          // `--key _xsell_source` read of scripts/attach-offer-attribution.js
          // covers both. Every band passes no key and is unchanged.
          const propertyKey = this.dataset.propertyKey || '_attach_source';
          formData.append(`properties[${propertyKey}]`, this.dataset.attachSource);
        }
        formData.append('sections', sections.map((entry) => entry.section).join(','));
        formData.append('sections_url', window.location.pathname);

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        this.setLoading(true);
        this.showError(null);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'cart-attach-add',
                productVariantId: variantId,
                errors: response.description,
                message: response.message,
              });
              this.showError(response.description || response.message);
              return;
            }

            // An add can only ever take the cart from empty to non-empty, so the
            // flags go false unconditionally. syncEmptyState runs AFTER the swap
            // because the .drawer__inner render recreates <cart-drawer-items>.
            CartRegions.renderSections(sections, response.sections);
            CartRegions.syncEmptyState(false);

            /*
             * `source: 'cart-items'` is deliberate and it is NOT a lie about
             * provenance — it is the only value cart.js:31 treats as "the cart
             * line regions are already rendered, do not re-fetch them", and it is
             * what CartItems.updateQuantity itself publishes (cart.js:143) after
             * rendering its own four regions. This element is in exactly that
             * position. Provenance rides on `origin` instead.
             *
             * Getting this wrong is not cosmetic — measured 2026-08-25. Publishing
             * any other source wakes CartDrawerItems.onCartUpdate(), which
             * re-renders .drawer__inner a network round-trip after this element
             * already rendered it — detaching the focus target out from under
             * afterAdd(). Until 2026-08-25 it was worse: CartDrawerItems inherited
             * CartItems.onCartUpdate and pasted CART-PAGE markup into the drawer.
             * That is fixed (cart-drawer.js), but the wasted refetch and the
             * focus race remain, so the flag still matters.
             *
             * The other two subscribers are unaffected: product-info.js:20
             * refreshes quantity rules on any source (wanted), and
             * recipient-form.js:25 acts only on 'product-form' (correctly inert).
             */
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-items',
              origin: 'cart-attach-add',
              productVariantId: variantId,
            });
            CartAttachAdd.afterAdd(inDrawer, variantId, announcement);
          })
          .catch((error) => {
            console.error(error);
            this.showError(window.cartStrings ? window.cartStrings.error : null);
          })
          .finally(() => {
            this.setLoading(false);
          });
      }

      setLoading(isLoading) {
        if (!this.button) return;
        const spinner = this.button.querySelector('.loading-overlay__spinner');
        this.button.classList.toggle('loading', isLoading);
        if (isLoading) {
          this.button.setAttribute('aria-disabled', 'true');
        } else {
          this.button.removeAttribute('aria-disabled');
        }
        if (spinner) spinner.classList.toggle('hidden', !isLoading);
      }

      showError(message) {
        const wrapper = this.querySelector('[data-attach-error]');
        if (!wrapper) return;
        wrapper.toggleAttribute('hidden', !message);
        wrapper.textContent = message || '';
      }

      /*
       * D2 + M7. Focus goes to the line item that was just added — identified by
       * the variant id its own quantity input already carries — and the
       * announcement goes to the live region belonging to the surface the shopper
       * is on. Both are per-add values, so a failed add leaves nothing behind.
       */
      static afterAdd(inDrawer, variantId, announcement) {
        const scope = document.getElementById(inDrawer ? 'CartDrawer' : 'main-cart-items');
        const region = document.getElementById(
          inDrawer ? 'CartDrawer-LiveRegionText' : 'cart-live-region-text'
        );
        if (region && announcement) region.textContent = announcement;
        if (!scope) return;

        const input = scope.querySelector(`[data-quantity-variant-id="${variantId}"]`);
        const line = input && input.closest('.cart-item');
        const target = line && line.querySelector('.cart-item__name');

        if (target) {
          // .cart-item__name is an <a href>, already in the tab order — do not
          // stamp tabindex on it, that would take it out.
          if (inDrawer) {
            trapFocus(scope, target);
          } else {
            target.focus();
          }
          return;
        }

        // Fallback only, and drawer-only: Dawn's own re-trap never fires after a
        // cart-add (open() re-adds classes the open drawer already has, so no
        // transitionend), which would drop focus to <body>.
        if (!inDrawer) return;
        const heading = scope.querySelector('.drawer__heading');
        if (!heading) return;
        heading.setAttribute('tabindex', '-1');
        trapFocus(scope, heading);
      }
    }
  );
}
